import axios from "axios";
import { API_URLS } from "@raydium-io/raydium-sdk-v2";
import { getAssociatedTokenAddress, NATIVE_MINT } from "@solana/spl-token";
import {
  LAMPORTS_PER_SOL,
  PublicKey,
  VersionedTransaction,
} from "@solana/web3.js";

const raydium = {
  namespaced: true,
  state: {},
  mutations: {},
  getters: {
    getQuoteData: () => async (payload) => {
      const { inputMint, outputMint, amount, slippage } = payload;
      const { data: quoteData } = await axios.get(
        `${
          API_URLS.SWAP_HOST
        }/compute/swap-base-in?inputMint=${inputMint}&outputMint=${outputMint}&amount=${amount}&slippageBps=${
          slippage * 100
        }&txVersion=V0`
      );
      return quoteData;
    },
  },
  actions: {
    async swapTransaction({ getters, rootState }, payload) {
      const { sender, inputMint, outputMint, amount } = payload;
      const [isInputSol, isOutputSol] = [
        inputMint === NATIVE_MINT.toBase58(),
        outputMint === NATIVE_MINT.toBase58(),
      ];
      const inputTokenAcc = await getAssociatedTokenAddress(
        new PublicKey(inputMint),
        sender.publicKey
      );
      const outputTokenAcc = await getAssociatedTokenAddress(
        new PublicKey(outputMint),
        sender.publicKey
      );
      const quoteData = await getters.getQuoteData({
        inputMint,
        outputMint,
        amount,
        slippage: rootState.solana.slippage,
      });

      const tx = {
        computeUnitPriceMicroLamports: String(
          rootState.solana.priorityFee * LAMPORTS_PER_SOL
        ),
        swapResponse: quoteData,
        txVersion: "V0",
        wallet: sender.publicKey.toBase58(),
        wrapSol: isInputSol,
        unwrapSol: isOutputSol,
        inputAccount: isInputSol ? undefined : inputTokenAcc?.toBase58(),
        outputAccount: isOutputSol ? undefined : outputTokenAcc?.toBase58(),
      };
      const { data: swapTransactions } = await axios.post(
        `${API_URLS.SWAP_HOST}/transaction/swap-base-in`,
        tx
      );
      const allTxBuf = swapTransactions.data.map((tx) =>
        Buffer.from(tx.transaction, "base64")
      );
      return allTxBuf.map((txBuf) => VersionedTransaction.deserialize(txBuf));
    },
    async buy({ rootState, dispatch, rootGetters }, payload) {
      const { wallet, amount } = payload;
      const sender = rootGetters["solana/getSender"](wallet.privateKey);
      const transactions = await dispatch("swapTransaction", {
        sender,
        inputMint: NATIVE_MINT.toBase58(),
        outputMint: rootState.solana.mint.toBase58(),
        amount: (amount * LAMPORTS_PER_SOL).toFixed(0),
      });
      for (const tx of transactions) {
        tx.sign([sender]);
        const signature = await rootState.solana.connection.sendTransaction(
          tx,
          { skipPreflight: true }
        );
        const txResult = await rootGetters["solana/getTxDetails"](signature);
        if (txResult) {
          return signature;
        }
      }
      throw new Error("Transaction failed!");
    },
    async sell({ rootState, dispatch, rootGetters }, payload) {
      const { wallet, amount } = payload;
      const sender = rootGetters["solana/getSender"](wallet.privateKey);
      const transactions = await dispatch("swapTransaction", {
        sender,
        inputMint: rootState.solana.mint.toBase58(),
        outputMint: NATIVE_MINT.toBase58(),
        amount: (
          amount * Math.pow(10, rootState.solana.metadata.decimals)
        ).toFixed(0),
      });
      for (const tx of transactions) {
        tx.sign([sender]);
        const signature = await rootState.solana.connection.sendTransaction(
          tx,
          { skipPreflight: true }
        );
        const txResult = await rootGetters["solana/getTxDetails"](signature);
        if (txResult) {
          return signature;
        }
      }
      throw new Error("Transaction failed!");
    },
  },
};

export default raydium;
